// ----------------------------------------------------------------
// bb-notify-form – handles back in stock notifications
// ----------------------------------------------------------------
class BBNotifyForm extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.input = this.querySelector('input');
		this.submitBtn = this.querySelector('button');
		this.infoBox = this.querySelector('.bb-info-text');
		this.successMessage = this.querySelector('.bb-notify-form__success');
		this.errorMessage = this.querySelector('.bb-notify-form__error');
		this.companyId = this.dataset.companyId || 'W4wgPX'; // Default to our Klaviyo test account if not set by Liquid
		// this.select = this.parentElement.querySelector('bb-size-select'); // First go to tab than search for a select component
		this.sizeSelect = this.parentElement?.querySelector('bb-size-select');
		this.onSizeChange = this.handleSizeChange.bind(this);
		this.onSubmit = this.handleSubmit.bind(this);
		this.sizeSelect?.addEventListener('size:change', this.onSizeChange);

		if (!this.sizeSelect) {
			console.warn('bb-notify-form must be used within a bb-tab so u can find bb-size-select component');
			return;
		}
		// this.activeVariantId = this.select.dataset.variantId || '';
		// this.option = this.select.dataset.option || '';
		// console.log('Active Variant ID:', this.activeVariantId, 'Option:', this.option);
		this.submitBtn?.addEventListener('click', this.onSubmit);
	}

	handleSizeChange = (e) => {
		const { variantId, status } = e.detail;
		const isBIS = status === 'bis';
		this.hidden = !isBIS;
		if (isBIS) this.variantId = variantId;
	};

	disconnectedCallback() {
		this.submitBtn?.removeEventListener('click', this.onSubmit);
		this.removeEventListener('notify', this.handleKlaviyoBIS);
	}

	async handleSubmit(event) {
		const email = this.input?.value;
		const variantId = this.variantId || '';

		if (!email || !variantId) return;

		try {
			await this.handleKlaviyoBIS(variantId, email);
			this.infoBox?.setAttribute('hidden', 'true');
			if (this.errorMessage) this.errorMessage.setAttribute('hidden', 'true');
			if (this.successMessage) this.successMessage.removeAttribute('hidden');
			if (this.input) this.input.setAttribute('hidden', 'true');
			if (this.submitBtn) this.submitBtn.setAttribute('hidden', 'true');
		} catch (error) {
			if (this.successMessage) this.successMessage.setAttribute('hidden', 'true');
			if (this.errorMessage) this.errorMessage.removeAttribute('hidden');
		}
	}

	buildKlaviyoGID(variantId) {
		return `$shopify:::$default:::${variantId}`;
	}

	async handleKlaviyoBIS(variantId, email) {
		const payload = this.buildKlaviyoPayload({ variantId, email, type: 'back-in-stock-subscription' });
		const res = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'revision': '2024-06-15',
			},
			body: JSON.stringify(payload),
		});
		if (res.status !== 202) {
			throw new Error(`Subscription failed for ${variantId} (status ${res.status})`);
		}
	}

	buildKlaviyoPayload({ variantId, email, type }) {
		return {
			data: {
				type,
				attributes: {
					profile: {
						data: {
							type: 'profile',
							attributes: { email },
						},
					},
					channels: ['EMAIL'],
				},
				relationships: {
					variant: {
						data: {
							type: 'catalog-variant',
							id: this.buildKlaviyoGID(variantId),
						},
					},
				},
			},
		};
	}
}

if (!customElements.get('bb-notify-form')) {
	customElements.define('bb-notify-form', BBNotifyForm);
}

// ----------------------------------------------------------------
// BbSizeSelect – custom dropdown (desktop) / bottom drawer (mobile)
// All HTML lives in Liquid; this class only wires events and
// toggles CSS classes / ARIA attributes.
// ----------------------------------------------------------------
class BbSizeSelect extends HTMLElement {
	connectedCallback() {
		this.trigger = this.querySelector('.bb-size-select__trigger');
		this.display = this.querySelector('.bb-size-select__display');
		this.panel = this.querySelector('.bb-size-select__panel');
		this.listbox = this.querySelector('.bb-size-select__listbox');
		this.backdrop = this.querySelector('.bb-size-select__backdrop');
		this.closeBtn = this.querySelector('.bb-size-select__close');
		this.optionItems = [...this.querySelectorAll('.bb-size-select__option')];
		this.variantId = '';

		if (!this.trigger || !this.panel) return;

		this.selectedValue = '';
		this.isOpen = false;
		this.placeholder = this.display?.textContent.trim() ?? '';

		this.bindTrigger();
		this.bindOptions();
		this.bindOutsideClick();

		this.backdrop?.addEventListener('click', () => this.close());
		this.closeBtn?.addEventListener('click', () => this.close());
	}

	disconnectedCallback() {
		this.offOutsideClick?.();
		if (this.isOpen) document.body.classList.remove('bb-scroll-lock');
	}

	get value() {
		return this.selectedValue;
	}

	isMobile() {
		return window.innerWidth < 670;
	}

	bindTrigger() {
		this.trigger.addEventListener('click', (e) => {
			e.stopPropagation();
			this.isOpen ? this.close() : this.open();
		});
		this.listbox?.addEventListener('keydown', (e) => this.handleKeydown(e));
	}

	bindOptions() {
		this.optionItems.forEach((li) => {
			li.addEventListener('click', () => this.pick(li.dataset.value));
		});
	}

	bindOutsideClick() {
		const handler = (e) => {
			if (!this.contains(e.target)) this.close();
		};
		document.addEventListener('click', handler);
		this.offOutsideClick = () => document.removeEventListener('click', handler);
	}

	open() {
		this.isOpen = true;
		this.classList.add('bb-size-select--open');
		this.panel.classList.add('bb-size-select__panel--open');
		this.trigger.setAttribute('aria-expanded', 'true');

		// This logic is a bit hacky to avoid scroll on mobile maybe remove it later cos it wasn't a requirement after all...
		if (this.isMobile()) {
			document.body.classList.add('bb-scroll-lock');
			if (this.closeBtn) {
				// position close button just above the drawer
				requestAnimationFrame(() => {
					const h = Math.min(this.panel.scrollHeight, window.innerHeight * 0.7);
					this.closeBtn.style.bottom = h + 12 + 'px';
				});
			}
		}

		const focused = this.listbox?.querySelector('.bb-size-select__option--selected') ?? this.optionItems[0];
		focused?.focus();
	}

	close() {
		this.isOpen = false;
		this.classList.remove('bb-size-select--open');
		this.panel.classList.remove('bb-size-select__panel--open');
		this.trigger.setAttribute('aria-expanded', 'false');
		document.body.classList.remove('bb-scroll-lock');
	}

	pick(value) {
		this.selectedValue = value;
		this.syncSelected(value);
		this.syncDisplay(value);
		this.dispatchEvent(new Event('change', { bubbles: true }));
		this.close();
		this.trigger.focus();
	}

	syncSelected(value) {
		this.optionItems.forEach((li) => {
			const active = li.dataset.value === value;
			li.classList.toggle('bb-size-select__option--selected', active);
			li.setAttribute('aria-selected', String(active));
		});
		this.setDataset();
	}

	syncDisplay(value) {
		if (!this.display) return;
		if (!value) {
			this.display.textContent = this.placeholder;
			this.trigger.classList.remove('bb-size-select__trigger--has-value');
			return;
		}
		const li = this.optionItems.find((l) => l.dataset.value === value);
		this.display.textContent = li?.querySelector('.bb-size-select__label')?.textContent ?? value;
		this.trigger.classList.add('bb-size-select__trigger--has-value');
	}

	// Called by BbConfigurator when selected color changes.
	// variantMap key format: `${size}|${color}` (matches buildchestbeltVariantMap)
	setAvailability(variantMap, color) {
		this.optionItems.forEach((li) => {
			const key = color ? `${li.dataset.value}|${color}` : li.dataset.value;
			const variant = variantMap?.get(key);
			let status = 'available';
			const variantId = variant?.id ? String(variant.id) : '';
			if (variant?.available && variant.pre_sell_active) status = 'presell';
			else if (!variant?.available) status = 'bis';
			li.dataset.status = status;
			li.dataset.variantId = variantId || '';
			li.classList.toggle('bb-size-select__option--presell', status === 'presell');
			li.classList.toggle('bb-size-select__option--bis', status === 'bis');
		});
	}

	reset() {
		this.selectedValue = '';
		this.variantId = '';
		this.dataset.variantId = '';
		this.dataset.option = '';
		this.syncSelected('');
		this.syncDisplay('');
	}

	setDataset() {
		// set data-variant-id and data-option on the trigger for Klaviyo BIS tracking
		const selectedOption = this.optionItems.find((li) => li.dataset.value === this.selectedValue);
		this.dataset.variantId = selectedOption?.dataset.variantId || '';
		this.dataset.option = selectedOption?.dataset.value || '';

		// Notify the BIS form in the same panel
		this.dispatchEvent(
			new CustomEvent('size:change', {
				bubbles: true,
				detail: { variantId: this.dataset.variantId, status: selectedOption?.dataset.status ?? 'available' },
			}),
		);
	}

	handleKeydown(e) {
		const idx = this.optionItems.indexOf(document.activeElement);
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			this.optionItems[Math.min(idx + 1, this.optionItems.length - 1)]?.focus();
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			this.optionItems[Math.max(idx - 1, 0)]?.focus();
		} else if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			if (document.activeElement.dataset?.value) this.pick(document.activeElement.dataset.value);
		} else if (e.key === 'Escape') {
			this.close();
			this.trigger.focus();
		}
	}
}

if (!customElements.get('bb-size-select')) {
	customElements.define('bb-size-select', BbSizeSelect);
}

if (!customElements.get('bb-configurator')) {
	// ----------------------------------------------------------------
	// VariantPicker – single instance, reused for every variant product
	// ----------------------------------------------------------------

	class VariantPicker {
		constructor(onConfirm) {
			this.onConfirm = onConfirm;
			this.ac = null;
		}

		open(dialog, data, triggerBtn, discount = null) {
			this.cleanup();
			this.dialog = dialog;
			this.data = data;
			this.triggerBtn = triggerBtn;
			this.selection = {};
			this.discount = discount;

			const sig = { signal: (this.ac = new AbortController()).signal };

			// Strip radio inputs + unlink labels so the theme's form mechanics
			// don't interfere with our button-driven selection state.
			dialog.querySelectorAll('.bb-variant-dialog__option-btn--swatch').forEach((btn) => {
				btn.querySelectorAll('input[type="radio"]').forEach((el) => el.remove());
				btn.querySelectorAll('label[for]').forEach((el) => el.removeAttribute('for'));
			});

			dialog.querySelectorAll('.bb-variant-dialog__option-btn[data-option-index]').forEach((btn) => {
				btn.addEventListener(
					'click',
					() => {
						this.onOptionClick(parseInt(btn.dataset.optionIndex, 10), btn.dataset.optionValue);
					},
					sig,
				);
			});

			dialog.querySelectorAll('select[data-option-index]').forEach((sel) => {
				sel.addEventListener(
					'change',
					() => {
						this.onOptionClick(parseInt(sel.dataset.optionIndex, 10), sel.value || undefined);
					},
					sig,
				);
			});

			dialog.querySelector('[data-action="confirm-variant"]')?.addEventListener('click', () => this.confirm(), sig);

			dialog
				.querySelector('[data-action="close-variant-dialog"]')
				?.addEventListener('click', () => dialog.close(), sig);

			// close on backdrop click (click lands on the <dialog> itself, not its children)
			dialog.addEventListener(
				'click',
				(e) => {
					if (e.target === dialog) dialog.close();
				},
				sig,
			);

			// clean up listeners when dialog closes (cancel or confirm)
			dialog.addEventListener('close', () => this.cleanup(), { signal: sig.signal, once: true });

			this.refreshUI();
			dialog.showModal();
		}

		cleanup() {
			this.ac?.abort();
			this.ac = null;
		}

		onOptionClick(optionIndex, value) {
			if (value === undefined || value === '') {
				delete this.selection[optionIndex];
			} else {
				this.selection[optionIndex] = value;
			}
			this.refreshUI();
		}

		// finds a variant only when ALL options are selected
		resolveVariant() {
			const { options, variants } = this.data;
			if (Object.keys(this.selection).length < options.length) return null;
			return variants.find((v) => options.every((_, i) => v[`option${i + 1}`] === this.selection[i])) ?? null;
		}

		isValuePresell(optionIndex, value) {
			const getOptionValue = { 0: (variant) => variant.option1, 1: (variant) => variant.option2, 2: (variant) => variant.option3 };
			return this.data.variants.some((variant) => {
				if (!variant.available || !variant.pre_sell_active) return false;
				if (getOptionValue[optionIndex](variant) !== value) return false;
				return this.data.options.every((_, optIdx) => {
					if (optIdx === optionIndex) return true;
					return this.selection[optIdx] === undefined || variant[`option${optIdx + 1}`] === this.selection[optIdx];
				});
			});
		}

		// a value is available if at least one available variant matches it
		// plus all other options already selected
		isValueAvailable(optionIndex, value) {
			const mapper = {
				0: (v) => v.option1,
				1: (v) => v.option2,
				2: (v) => v.option3,
			};
			// const optionKey = mapper[optionIndex];
			// if (!optionKey) {
			// 	console.warn('Unsupported option index:', optionIndex);
			// 	return false;
			// }
			return this.data.variants.some((v) => {
				if (!v.available) return false;
				// if (v[`option${optionIndex + 1}`] !== value) return false;
				if (mapper[optionIndex](v) !== value) return false;
				return this.data.options.every((_, i) => {
					if (i === optionIndex) return true;
					return this.selection[i] === undefined || v[`option${i + 1}`] === this.selection[i];
				});
			});
		}

		refreshUI() {
			this.data.options.forEach((_, i) => {
				const group = this.dialog.querySelector(`.bb-variant-dialog__option-group[data-option-index="${i}"]`);
				if (!group) return;

				const valueLabel = group.querySelector('.js-option-value');
				if (valueLabel) valueLabel.textContent = this.selection[i] ?? '';

				// option buttons (swatches + any block-style buttons)
				group.querySelectorAll('.bb-variant-dialog__option-btn[data-option-index]').forEach((btn) => {
					const v = btn.dataset.optionValue;
					const available = this.isValueAvailable(i, v);
					btn.classList.toggle('bb-variant-dialog__option-btn--selected', this.selection[i] === v);
					btn.classList.toggle('bb-variant-dialog__option-btn--unavailable', !available);
					btn.disabled = !available;
				});

				// size select
				const sel = group.querySelector('select[data-option-index]');
				if (sel) {
					const presellLabel = sel.dataset.presellLabel ?? 'Vorbestellen';
					[...sel.options].forEach((opt) => {
						if (!opt.value) return;
						const available = this.isValueAvailable(i, opt.value);
						const presell = available && this.isValuePresell(i, opt.value);
						opt.disabled = !available;
						opt.textContent = !available ? `✕ ${opt.value}` : presell ? `${opt.value} (${presellLabel})` : opt.value;
					});
					if (this.selection[i] !== undefined) sel.value = this.selection[i];
				}
			});

			const confirmBtn = this.dialog.querySelector('[data-action="confirm-variant"]');
			if (confirmBtn) {
				const ready = !!this.resolveVariant()?.available;
				confirmBtn.setAttribute('aria-disabled', String(!ready));
				// confirmBtn.disabled = !ready;
			}
		}

		confirm() {
			const variant = this.resolveVariant();
			if (!variant?.available) {
				this.showToolTip();
				return;
			}
			this.dialog.close();
			this.onConfirm({
				variantId: variant.id,
				productId: String(this.data.productId),
				triggerBtn: this.triggerBtn,
				discount: this.discount,
				price: String(variant.price),
				isPresell: !!(variant.available && variant.pre_sell_active),
				presellDate: variant.pre_sell_date || null,
			});
		}

		showToolTip() {
			console.log("Please select all options to confirm your choice");
			const tooltip = this.dialog.querySelector('.bb-tooltip');
			const message = this.dialog.querySelector('[data-info-message]')?.dataset.infoMessage ?? 'Please select all options';
			if (!tooltip || !message) return;
			tooltip.textContent = message;
			tooltip.hidden = false;
			setTimeout(() => {
				tooltip.hidden = true;
			}, 3000);
		}


	}

	// ----------------------------------------------------------------
	// UpsellTab – owns Tab 3 item selection state
	// ----------------------------------------------------------------

	class UpsellTab {
		constructor(configurator, upsellState) {
			this.configurator = configurator;
			this.state = upsellState;

			this.picker = new VariantPicker(({ variantId, price, productId, triggerBtn, discount, isPresell, presellDate }) => {
				this.addItem(productId, String(variantId), discount, price, isPresell, presellDate);
				this.setAdded(triggerBtn, true);
			});

			this.initItems();
		}

		initItems() {
			this.configurator.querySelectorAll('.bb-upsell-item').forEach((li) => {
				const { productId, hasVariants, preselected, variantId, discount, price } = li.dataset;
				const btn = li.querySelector('.bb-upsell-item__btn');
				if (!btn) return;

				// seed state from server-rendered preselected items
				if (preselected === 'true' && hasVariants !== 'true' && variantId) {
					this.addItem(productId, variantId, discount, price);
					// aria-pressed="true" already rendered by Liquid, no DOM change needed
				}

				if (hasVariants === 'true') {
					btn.addEventListener('click', () => {
						const isAdded = btn.getAttribute('aria-pressed') === 'true';
						if (isAdded) {
							this.removeItem(productId);
							this.setAdded(btn, false);
						} else {
							this.openPicker(li, btn, discount, price);
						}
					});
				} else {
					btn.addEventListener('click', () => {
						const added = btn.getAttribute('aria-pressed') === 'true';
						if (added) {
							this.removeItem(productId);
						} else {
							this.addItem(productId, variantId, discount, price);
						}
						this.setAdded(btn, !added);
					});
				}
			});
		}

		openPicker(li, btn, discount = null, price = null) {
			const dialog = li.querySelector('.bb-variant-dialog');
			const dataEl = li.querySelector('.bb-upsell-variants');
			if (!dialog || !dataEl) return;
			try {
				this.picker.open(dialog, JSON.parse(dataEl.textContent), btn, discount, price);
			} catch (e) {
				console.error('Failed to parse variant data', e);
			}
		}

		addItem(productId, variantId, discount = null, price = null, isPresell = false, presellDate = null) {
			this.state.items = this.state.items.filter((item) => item.productId !== productId);
			this.state.items.push({ productId, variantId, discount, price, isPresell, presellDate });
			this.configurator.updatePrice();
		}

		removeItem(productId) {
			this.state.items = this.state.items.filter((i) => i.productId !== productId);
			this.configurator.updatePrice();
		}

		setAdded(btn, added) {
			btn.setAttribute('aria-pressed', String(added));
		}

		getItems() {
			return this.state.items;
		}

		reset() {
			this.state.items = [];
			this.state.confirmed = false;
			this.configurator.querySelectorAll('.bb-upsell-item').forEach((li) => {
				const btn = li.querySelector('.bb-upsell-item__btn');
				if (!btn) return;
				const isPreselected = li.dataset.preselected === 'true' && li.dataset.hasVariants !== 'true' && li.dataset.variantId;
				if (isPreselected) {
					this.addItem(li.dataset.productId, li.dataset.variantId, li.dataset.discount, li.dataset.price);
				}
				btn.setAttribute('aria-pressed', isPreselected ? 'true' : 'false');
			});
		}
	}

	// ----------------------------------------------------------------
	// BBConfigurator
	// ----------------------------------------------------------------

	class BBConfigurator extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.state = {
				coat: { productId: null, size: null, variantId: null, available: false, confirmed: false, price: null, isPresell: false },
				chestbelt: { color: null, size: null, variantId: null, available: false, confirmed: false, price: null, isPresell: false },
				upsell: { items: [], confirmed: false },
			};

			this.currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
				style: 'currency',
				currency: Shopify.currency.active,
			});

			this.maxDiff = parseInt(this.dataset.compatibilityDiff) || 2;
			this.sizesAreCompatible = false;
			this.footerVisible = false;
			this.atcBtnVisible = true;
			this.tabsVisible = true;
			this.stickyAtcWrapper = null;

			this.initTabs();
			this.initInfoIcons();
			this.initMeasureGuides();
			this.initCoatTab();
			this.initchestbeltTab();
			this.upsellTab = new UpsellTab(this, this.state.upsell);
			this.initAddToCart();
			this.initFooterObserver();
			this.initTabsObserver();
			this.initStickyConfirm();
			this.initStickyATC();
			this.applyDeepLinkParams();
		}

		// ----------------------------------------------------------------
		// Tabs
		// ----------------------------------------------------------------

		initTabs() {
			this.tabs = [...this.querySelectorAll('.bb-tabs__tab')];
			this.panels = [...this.querySelectorAll('.bb-tab-panel')];

			this.tabs.forEach((tab) => {
				tab.addEventListener('click', () => this.activateTab(tab.dataset.tab));
			});
		}

		// ----------------------------------------------------------------
		// Debugging helper to inspect the current state of the configurator
		// ----------------------------------------------------------------

		getState() {
			return this.state;
		}

		updateState(updates) {
			this.state = { ...this.state, ...updates };
			this.updatePrice();
		}

		activateTab(tabId) {
			this.tabs.forEach((tab) => {
				const active = tab.dataset.tab === tabId;
				tab.setAttribute('aria-selected', String(active));
				tab.classList.toggle('bb-tabs__tab--active', active);
				tab.tabIndex = active ? 0 : -1;
			});

			this.panels.forEach((panel) => {
				const active = panel.id === `bb-panel-${tabId}`;
				panel.classList.toggle('bb-tab-panel--active', active);
				panel.hidden = !active;
			});

			// Remove sticky from any confirm button that's no longer in the active tab
			this.querySelectorAll('.bb-step-actions__wrapper').forEach((w) => {
				if (!w.closest('.bb-tab-panel--active')) {
					w.classList.remove('bb-step-actions--sticky');
				}
			});
		}

		// ----------------------------------------------------------------
		// Info icon tooltips (generic - works for all steps)
		// ----------------------------------------------------------------

		initInfoIcons() {
			this.querySelectorAll('.bb-info-icon').forEach((btn) => {
				const tooltipId = btn.getAttribute('aria-controls');
				const tooltip = tooltipId ? this.querySelector(`#${tooltipId}`) : null;
				if (!tooltip) return;

				btn.addEventListener('click', (e) => {
					e.stopPropagation();
					const isOpen = btn.getAttribute('aria-expanded') === 'true';
					this.closeAllInfoTooltips();
					if (!isOpen) this.openInfoTooltip(btn, tooltip);
				});
			});

			document.addEventListener('click', () => this.closeAllInfoTooltips());
		}

		openInfoTooltip(btn, tooltip) {
			btn.setAttribute('aria-expanded', 'true');
			tooltip.hidden = false;
		}

		closeAllInfoTooltips() {
			this.querySelectorAll('.bb-info-icon[aria-expanded="true"]').forEach((btn) => {
				btn.setAttribute('aria-expanded', 'false');
				const tooltip = this.querySelector(`#${btn.getAttribute('aria-controls')}`);
				if (tooltip) tooltip.hidden = true;
			});
		}

		// ----------------------------------------------------------------
		// Measure guide - sync aria-expanded + auto-open on scroll into view
		// ----------------------------------------------------------------

		initMeasureGuides() {
			// Set to true to prevent auto-reopen after the user manually closes the guide
			const RESPECT_USER_CLOSE = true;

			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (!entry.isIntersecting) return;

						// boundingClientRect.top > 0 → element entered from the bottom = scrolling down
						const scrollingDown = entry.boundingClientRect.top > 0;
						if (!scrollingDown) return;

						const details = entry.target;
						if (RESPECT_USER_CLOSE && details.dataset.userClosed) return;

						if (!details.open) {
							details.open = true;
							details.setAttribute('aria-expanded', 'true');
						}
					});
				},
				{ threshold: 1.0 },
			);

			this.querySelectorAll('details.group').forEach((details) => {
				details.addEventListener('toggle', () => {
					details.setAttribute('aria-expanded', String(details.open));

					// Track when the user explicitly closes the guide
					if (RESPECT_USER_CLOSE && !details.open) {
						details.dataset.userClosed = 'true';
					}
				});

				observer.observe(details);
			});
		}

		// ----------------------------------------------------------------
		// State - confirm a step
		// ----------------------------------------------------------------

		confirmStep(tabId) {
			this.state[tabId].confirmed = true;

			const tab = this.tabs.find((t) => t.dataset.tab === tabId);
			if (tab) tab.classList.add('bb-tabs__tab--confirmed');
			this.scrollToElement(tab);
			this.activateATCButton();
		}

		// ----------------------------------------------------------------
		// Deep link pre-selection (Klaviyo)
		// URL format: /products/mantel-schwarz?bb_coat_size=l&bb_chestbelt_color=beige&bb_chestbelt_size=m
		// ----------------------------------------------------------------

		applyDeepLinkParams() {
			const p = new URLSearchParams(location.search);
			const coatSize = p.get('bb_coat_size');
			const chestbeltColor = p.get('bb_chestbelt_color');
			const chestbeltSize = p.get('bb_chestbelt_size');

			if (!coatSize && !chestbeltColor && !chestbeltSize) return;

			const coatApplied = coatSize ? this.applyCoatDeepLink(coatSize) : false;
			const chestbeltApplied = (chestbeltColor && chestbeltSize) ? this.applyChestbeltDeepLink(chestbeltColor, chestbeltSize) : false;

			if (coatApplied && chestbeltApplied) {
				this.confirmStep('coat');
				this.confirmStep('chestbelt');
				this.activateTab('upsell');
			} else if (coatApplied) {
				this.confirmStep('coat');
				this.activateTab('chestbelt');
			}
		}

		normalizeSize(str) {
			return str.split('|')[0].trim().toLowerCase();
		}

		applyCoatDeepLink(size) {
			const sizeSelect = this.querySelector('#bb-panel-coat bb-size-select');
			if (!sizeSelect) return false;

			const needle = this.normalizeSize(size);
			const option = sizeSelect.optionItems.find((li) => this.normalizeSize(li.dataset.value) === needle);
			console.log('[BB deeplink] coat: needle=', needle, '| match=', option?.dataset.value ?? 'none');
			if (!option) return false;

			sizeSelect.pick(option.dataset.value);
			console.log('[BB deeplink] coat state after pick:', JSON.stringify(this.state.coat));
			return this.state.coat.available;
		}

		applyChestbeltDeepLink(color, size) {
			const chestbeltPanel = this.querySelector('#bb-panel-chestbelt');
			const sizeSelect = chestbeltPanel?.querySelector('bb-size-select');
			if (!sizeSelect) return false;

			const colorBtn = chestbeltPanel.querySelector(`.bb-color-swatches__item--chestbelt[data-color="${color.trim().toLowerCase()}"]`);
			console.log('[BB deeplink] chestbelt: color=', color.trim().toLowerCase(), '| colorBtn=', colorBtn ?? 'none');
			if (!colorBtn) return false;
			colorBtn.click();

			const needle = this.normalizeSize(size);
			const option = sizeSelect.optionItems.find((li) => this.normalizeSize(li.dataset.value) === needle);
			console.log('[BB deeplink] chestbelt: size needle=', needle, '| match=', option?.dataset.value ?? 'none');
			if (!option) return false;

			sizeSelect.pick(option.dataset.value);
			console.log('[BB deeplink] chestbelt state after pick:', JSON.stringify(this.state.chestbelt));
			return this.state.chestbelt.available;
		}

		// ----------------------------------------------------------------
		// Validation tooltip
		// ----------------------------------------------------------------

		showTooltip(btn, message, callback = null) {
			const tooltip = btn.closest('.bb-step-actions__wrapper, .sticky-atc-wrapper')?.querySelector('.bb-tooltip');
			if (!tooltip) return;

			clearTimeout(this.tooltipTimer);
			tooltip.textContent = message;
			tooltip.hidden = false;
			// TODO: remove or change behavior cos not accessible by self closing after timeout
			this.tooltipTimer = setTimeout(() => {
				tooltip.hidden = true;
				if (callback) callback();
			}, 3000);
		}

		hideTooltip(btn) {
			const tooltip = btn.closest('.bb-step-actions__wrapper, .sticky-atc-wrapper')?.querySelector('.bb-tooltip');
			if (tooltip) tooltip.hidden = true;
			clearTimeout(this.tooltipTimer);
		}

		scrollToElement(element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}

		// ----------------------------------------------------------------
		// Tab 1 - Coat
		// ----------------------------------------------------------------

		buildCoatVariantMap() {
			// Variants JSON blob
			const blob = this.querySelector('#bb-coat-variants');
			if (!blob) return new Map();
			try {
				const variants = JSON.parse(blob.textContent);
				return new Map(variants.map((v) => [v.size, v]));
			} catch {
				return new Map();
			}
		}

		resolveCoatVariant(size) {
			return this.coatVariantMap?.get(size) ?? null;
		}

		syncCoatVariant() {
			const { size } = this.state.coat;
			if (!size) {
				this.state.coat.variantId = null;
				this.state.coat.available = false;
				this.state.coat.isPresell = false;
				this.updatePrice();
				return;
			}
			const resolved = this.resolveCoatVariant(size);
			this.state.coat.variantId = resolved?.id != null ? String(resolved.id) : null;
			this.state.coat.available = resolved?.available ?? false;
			this.state.coat.isPresell = resolved?.available && resolved?.pre_sell_active;
			this.state.coat.presellDate = resolved?.pre_sell_date || null;
			this.updatePrice();
		}

		updateCoatConfirm() {
			const confirmBtn = this.querySelector('#bb-confirm-coat');
			if (!confirmBtn) return;
			this.syncCoatVariant();
			const colorChosen = !!this.state.coat.productId;
			// If not available but also not no presell it is BIS so don't activate confirm button
			const ready = colorChosen && !!this.state.coat.variantId && this.state.coat.available;
			confirmBtn.setAttribute('aria-disabled', String(!ready));
		}

		initCoatTab() {
			const coatPanel = this.querySelector('#bb-panel-coat');
			const colorBtns = [...coatPanel.querySelectorAll('.bb-color-swatches__item')];
			const sizeSelect = coatPanel.querySelector('bb-size-select');
			const confirmBtn = this.querySelector('#bb-confirm-coat');

			if (!sizeSelect || !confirmBtn) return;

			this.coatVariantMap = this.buildCoatVariantMap();
			sizeSelect.setAvailability(this.coatVariantMap);

			// Color is pre-selected via the current PDP product (color change = page reload)
			const preselected = colorBtns.find((b) => b.classList.contains('bb-color-swatches__item--selected'));
			this.state.coat.productId = preselected?.dataset.productId ?? null;
			this.state.coat.size = sizeSelect.value || null;
			this.state.coat.isPresell = false;

			sizeSelect.addEventListener('change', () => {
				this.state.coat.size = sizeSelect.value || null;
				this.hideTooltip(confirmBtn);
				this.updateCoatConfirm();
			});

			confirmBtn.addEventListener('click', () => {
				if (confirmBtn.getAttribute('aria-disabled') === 'true') {
					const colorChosen = !!this.state.coat.productId;
					const msg = !colorChosen ? confirmBtn.dataset.errorColor : confirmBtn.dataset.errorSize;
					this.showTooltip(confirmBtn, msg);
					return;
				}
				this.hideTooltip(confirmBtn);
				this.confirmStep('coat');
				this.activateTab(confirmBtn.dataset.nextTab);
			});

			this.updateCoatConfirm();
		}

		// ----------------------------------------------------------------
		// Tab 2 - chestbelt
		// ----------------------------------------------------------------

		buildchestbeltVariantMap() {
			const blob = this.querySelector('#bb-chestbelt-variants');
			if (!blob) return new Map();
			try {
				const variants = JSON.parse(blob.textContent);
				return new Map(variants.map((v) => [`${v.size}|${v.color}`, v]));
			} catch {
				return new Map();
			}
		}

		colorSwatchImageHandler(colorSwatch) {
			const color = colorSwatch.dataset.color;
			const dots = document.querySelector('scroll-shadow page-dots');
			if (!dots) return;

			const img = dots.querySelector(`img[alt*='${color}']`);
			const dot = img?.parentElement;

			if (!dot) {
				console.warn('No carousel image found for color:', color);
				return;
			}

			dot.click();
		}

		compatibilityCheck() {
			const infoBox = this.querySelector('#bb-compatibility-warning');
			const coatSize = this.state.coat.size?.split('|')[0].trim();
			const chestbeltSize = this.state.chestbelt.size?.split('|')[0].trim();
			if (!coatSize || !chestbeltSize) {
				infoBox && (infoBox.hidden = true);
				return;
			}

			const coatSizeNumber = parseInt(coatSize, 10);
			const chestbeltSizeNumber = parseInt(chestbeltSize, 10);
			const diff = Math.abs(coatSizeNumber - chestbeltSizeNumber);

			if (isNaN(coatSizeNumber) || isNaN(chestbeltSizeNumber)) {
				infoBox && (infoBox.hidden = true);
				return;
			}
			if (diff > this.maxDiff) {
				console.warn(
					`Selected chestbelt size ${chestbeltSize} may not be compatible with coat size ${coatSize}. Size difference of ${diff} exceeds max allowed of ${this.maxDiff}.`,
				);
				// Optionally, you could show a warning message to the user here
				// Show infoBox with warning message
				infoBox ? (infoBox.hidden = false) : null;
			} else {
				infoBox ? (infoBox.hidden = true) : null;
			}
		}

		resolvechestbeltVariant(color, size) {
			const key = `${size}|${color}`;
			return this.chestbeltVariantMap?.get(key) ?? null;
		}

		syncchestbeltVariant() {
			const { color, size } = this.state.chestbelt;
			if (!color || !size) {
				this.state.chestbelt.variantId = null;
				this.state.chestbelt.available = false;
				this.state.chestbelt.isPresell = false;
				this.updatePrice();
				return;
			}
			const resolved = this.resolvechestbeltVariant(color, size);
			this.state.chestbelt.variantId = resolved?.id ?? null;
			this.state.chestbelt.available = resolved?.available ?? false;
			this.state.chestbelt.isPresell = resolved?.available && resolved?.pre_sell_active;
			this.state.chestbelt.presellDate = resolved?.pre_sell_date || null;
			this.updatePrice();
		}

		updatechestbeltConfirm() {
			const confirmBtn = this.querySelector('#bb-confirm-chestbelt');
			if (!confirmBtn) return;
			this.compatibilityCheck();
			this.syncchestbeltVariant();
			this.updatePresellInfo();
			const ready = !!this.state.chestbelt.color && !!this.state.chestbelt.size && this.state.chestbelt.available;
			confirmBtn.setAttribute('aria-disabled', String(!ready));
		}

		updatePresellInfo() {
			const infoBox = this.querySelector('#bb-presell-info');
			if (!infoBox) return;

			const { color, size } = this.state.chestbelt;
			const variant = color && size ? this.resolvechestbeltVariant(color, size) : null;

			// pre_sell_active is only meaningful when available: true (continue selling must be on per variant)
			const isPresell = variant?.available && variant?.pre_sell_active;
			if (!isPresell) {
				infoBox.hidden = true;
				return;
			}

			if (variant.pre_sell_date) {
				const formatted = new Date(variant.pre_sell_date).toLocaleDateString('de-DE', {
					day: 'numeric',
					month: 'long',
					year: 'numeric',
				});
				const body = infoBox.querySelector('.bb-info-text__body');
				if (body) body.textContent = (infoBox.dataset.label ?? '').replace('{{date}}', formatted);
			} else {
				const body = infoBox.querySelector('.bb-info-text__body');
				if (body) body.textContent = infoBox.dataset.labelDefault ?? '';
			}

			infoBox.hidden = false;
		}

		initchestbeltTab() {
			const chestbeltPanel = this.querySelector('#bb-panel-chestbelt');
			const colorBtns = [...chestbeltPanel.querySelectorAll('.bb-color-swatches__item--chestbelt')];
			const sizeSelect = chestbeltPanel.querySelector('bb-size-select');
			const confirmBtn = this.querySelector('#bb-confirm-chestbelt');

			if (!sizeSelect || !confirmBtn) return;

			this.chestbeltVariantMap = this.buildchestbeltVariantMap();
			this.state.chestbelt.color = null;
			this.state.chestbelt.size = sizeSelect.value || null;
			this.state.chestbelt.isPresell = false;

			colorBtns.forEach((btn) => {
				btn.addEventListener('click', () => {
					this.colorSwatchImageHandler(btn);
					colorBtns.forEach((b) => {
						b.classList.remove('bb-color-swatches__item--selected');
						b.setAttribute('aria-pressed', 'false');
					});
					btn.classList.add('bb-color-swatches__item--selected');
					btn.setAttribute('aria-pressed', 'true');
					this.state.chestbelt.color = btn.dataset.color ?? null;
					sizeSelect.setAvailability(this.chestbeltVariantMap, this.state.chestbelt.color);
					if (sizeSelect.value) sizeSelect.setDataset();
					this.updatechestbeltConfirm();
				});
			});

			sizeSelect.addEventListener('change', () => {
				this.state.chestbelt.size = sizeSelect.value || null;
				this.hideTooltip(confirmBtn);
				this.updatechestbeltConfirm();
			});

			confirmBtn.addEventListener('click', () => {
				if (confirmBtn.getAttribute('aria-disabled') === 'true') {
					const msg = !this.state.chestbelt.color ? confirmBtn.dataset.errorColor : confirmBtn.dataset.errorSize;
					this.showTooltip(confirmBtn, msg);
					return;
				}
				this.hideTooltip(confirmBtn);
				this.confirmStep('chestbelt');
				this.activateTab(confirmBtn.dataset.nextTab);
			});

			this.updatechestbeltConfirm();
		}

		// ----------------------------------------------------------------
		// Sticky confirm buttons (Tab 1 + 2)
		// ----------------------------------------------------------------<

		initStickyConfirm() {
			this.stickyObserver = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						const sentinel = entry.target;
						const wrapper = sentinel.nextElementSibling;
						if (!wrapper.closest('.bb-tab-panel--active')) continue;
						const scrolledAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
						if (scrolledAbove) {
							sentinel.style.height = wrapper.offsetHeight + 'px';
							wrapper.classList.add('bb-step-actions--sticky');
							// wrapper.style.width = wrapper.offsetWidth + 'px';
						} else {
							sentinel.style.height = '';
							wrapper.classList.remove('bb-step-actions--sticky');
							// wrapper.style.width = '100%';
						}
					}
				},
				{ threshold: 1 },
			);

			['#bb-confirm-coat', '#bb-confirm-chestbelt'].forEach((id) => {
				const wrapper = this.querySelector(id)?.closest('.bb-step-actions__wrapper');
				if (!wrapper) return;
				const sentinel = document.createElement('div');
				wrapper.insertAdjacentElement('beforebegin', sentinel);
				this.stickyObserver.observe(sentinel);
			});
		}

		// ----------------------------------------------------------------
		// Footer observer – shared state for all sticky elements
		// ----------------------------------------------------------------

		initFooterObserver() {
			const footer = document.querySelector('.shopify-section--footer');
			if (!footer) return;
			new IntersectionObserver(
				([entry]) => {
					this.footerVisible = entry.isIntersecting;
					if (this.stickyAtcWrapper) {
						this.stickyAtcWrapper.hidden = this.atcBtnVisible || this.footerVisible;
					}

					this.querySelectorAll('.bb-step-actions--sticky').forEach((w) => {
						w.hidden = this.footerVisible || this.tabsVisible;
					});
				},
				{ threshold: 0 },
			).observe(footer);
		}

		initTabsObserver() {
			const tabs = this.querySelector('.bb-tabs');
			new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
							this.tabsVisible = entry.isIntersecting;
					});
				},
				{ threshold: 0 },
			).observe(tabs);
		}

		// ----------------------------------------------------------------
		// Sticky ATC (Tab 3)
		// ----------------------------------------------------------------

		initStickyATC() {
			const atcBtn = this.querySelector('#bb-upsell-atc');
			const stickyWrapper = this.querySelector('.sticky-atc-wrapper');
			if (!atcBtn || !stickyWrapper) return;

			this.stickyAtcWrapper = stickyWrapper;

			const stickyAtcBtn = stickyWrapper.querySelector('#bb-upsell-sticky-atc');
			if (stickyAtcBtn) {
				stickyAtcBtn.addEventListener('click', this.addToCart.bind(this));
			}

			this.stickyAtcObserver = new IntersectionObserver(
				([entry]) => {
					this.atcBtnVisible = entry.isIntersecting;
					stickyWrapper.hidden = this.atcBtnVisible || this.footerVisible;
				},
				{ threshold: 0 },
			);

			this.stickyAtcObserver.observe(atcBtn);
		}

		// ----------------------------------------------------------------
		// Tab 3 - ATC
		// ----------------------------------------------------------------

		initAddToCart() {
			this.atcBtn = this.querySelector('#bb-upsell-atc');
			this.stickyAtc = this.querySelector('#bb-upsell-sticky-atc');
			if (!this.atcBtn) return;

			this.atcBtn.addEventListener('click', this.addToCart.bind(this));
		}

		async addToCart(event) {
			const atc = event.currentTarget;
			if (!this.confirmATCState(atc)) return;
			atc.setAttribute('aria-busy', 'true');
			atc.disabled = true;

			try {
				const items = this.buildCartPayload();
				if (!items.length) return;

				const rawResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ items }),
				});

				if (!rawResponse.ok) {
					const err = await rawResponse.json().catch(() => ({}));
					console.error('cart/add.js failed:', rawResponse.status, err?.description ?? err);
					return;
				}

				await rawResponse.json();
				this.openCartDrawer();
				this.reset();
			} finally {
				atc.removeAttribute('aria-busy');
				atc.disabled = false;
			}
		}

		openCartDrawer() {
			document.querySelectorAll('cart-count').forEach((el) => el.classList.remove('opacity-0'));
			document.querySelector('cart-drawer')?.show();
			document.dispatchEvent(new CustomEvent('cart:refresh'));
		}

		_bbPresellLine(dateStr) {
			if (!dateStr) return {};
			const s = String(dateStr);
			const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
			const human = m ? `${m[3]}.${m[2]}.${m[1]}` : s;
			return { Vorbestellung: 'Lieferung ab ' + human };
		}

		buildCartPayload() {
			const payload = [];
			if (this.state.coat.variantId) {
				const properties = this.state.coat.isPresell
					? { _bb_upsell: 'core', _bb_presell: 'true', _bb_product_type: 'coat', ...this._bbPresellLine(this.state.coat.presellDate) }
					: { _bb_upsell: 'core', _bb_product_type: 'coat' };
				payload.push({ id: this.state.coat.variantId, quantity: 1, properties });
			}
			if (this.state.chestbelt.variantId) {
				const properties = this.state.chestbelt.isPresell
					? { _bb_upsell: 'core', _bb_presell: 'true', _bb_product_type: 'chestbelt', ...this._bbPresellLine(this.state.chestbelt.presellDate) }
					: { _bb_upsell: 'core', _bb_product_type: 'chestbelt' };
				payload.push({ id: this.state.chestbelt.variantId, quantity: 1, properties });
			}
			this.state.upsell.items.forEach((item) => {
				if (item.variantId && item.discount != null) {
					const properties = item.isPresell
						? { _bb_upsell: item.discount + '_gift', _bb_discount: item.discount, _bb_presell: 'true', ...this._bbPresellLine(item.presellDate) }
						: { _bb_upsell: item.discount + '_gift', _bb_discount: item.discount };
					payload.push({ id: item.variantId, quantity: 1, properties });
				}
			});
			return payload;
		}

		confirmATCState(btn = this.atcBtn) {
			if (!this.state.coat.confirmed) {
				this.showTooltip(btn, this.atcBtn.dataset.errorTabOneMissing);
				return false;
			}
			if (!this.state.chestbelt.confirmed) {
				this.showTooltip(btn, this.atcBtn.dataset.errorTabTwoMissing);
				return false;
			}

			return true;
		}

		activateATCButton() {
			if (!this.atcBtn) return;
			const ready = this.state.coat.confirmed && this.state.chestbelt.confirmed;
			if (this.state.coat.isPresell || this.state.chestbelt.isPresell) {
				// Change ATC button text to "Pre-order now" if either coat or chestbelt is a pre-sell item
				this.atcBtn.textContent = this.atcBtn.dataset.preOrderAtc ?? this.atcBtn.textContent;
			}

			this.atcBtn.setAttribute('aria-disabled', String(!ready));
			this.stickyAtc.setAttribute('aria-disabled', String(!ready));
		}

		// ----------------------------------------------------------------
		// Price updater
		// ----------------------------------------------------------------

		calculateTotalPrice() {
			let total = 0;

			if (this.state.coat.variantId) {
				const coatVariant = this.resolveCoatVariant(this.state.coat.size);
				total += coatVariant?.price ?? 0;
			}

			if (this.state.chestbelt.variantId) {
				const chestbeltVariant = this.resolvechestbeltVariant(this.state.chestbelt.color, this.state.chestbelt.size);
				total += chestbeltVariant?.price ?? 0;
			}

			this.state.upsell.items.forEach((item) => {
				console.log('Calculating price for upsell item:', item);
				const price = parseFloat(item.price) || 0;
				const discount = parseFloat(item.discount) || 0;
				if (discount >= 100) return; // free item
				total += discount > 0 ? Math.round((price * (100 - discount)) / 100) : price;
			});

			return total; // Shopify cents
		}

		formatMoney(cents) {
			return this.currencyFormatter.format(cents / 100);
		}

		updatePrice() {
			const total = this.calculateTotalPrice();
			if (total === 0) return;
			const formatted = this.formatMoney(total);
			this.querySelectorAll('.sticky-price, .bb-price').forEach((el) => {
				el.textContent = " - " + formatted;
			});
		}

		reset(){
			const coatProductId = this.dataset.productId ?? null;

			// selector remains on the previously selected chestbelt color.
			// so we need to clear it manually.
			this.querySelectorAll('.bb-color-swatches__item--chestbelt').forEach((btn) => {
				btn.classList.remove('bb-color-swatches__item--selected');
				btn.setAttribute('aria-pressed', 'false');
			});

			this.state = {
				coat: { productId: coatProductId, size: null, variantId: null, available: false, confirmed: false, price: null, isPresell: false },
				chestbelt: { color: null, size: null, variantId: null, available: false, confirmed: false, price: null, isPresell: false },
				upsell: { items: [], confirmed: false },
			};
			this.upsellTab.reset();
			this.querySelectorAll('bb-size-select').forEach((sel) => sel.reset());
			this.tabs.forEach((tab) => {
				tab.classList.remove('bb-tabs__tab--confirmed');
			});
			// Re-evaluate both confirm buttons so they reflect "no size chosen yet"
			// rather than inheriting stale state from the previous configuration.
			this.updateCoatConfirm();
			this.updatechestbeltConfirm();
			this.activateTab('coat');
		}
	}

	customElements.define('bb-configurator', BBConfigurator);
}
